/**
 * Markdown (an agent's report), drawn in the app's type: headings, paragraphs, lists, quotes, code, tables, links.
 * Items and people named in it show as in comments (chips). See lib/markdown.ts for what's understood.
 */

import type { SpaceOut } from "../../api/types.gen.ts";
import { type Align, type Block, blocks, type Inline } from "../../lib/markdown.ts";
import { LINK } from "../../ui/styles.ts";
import { RichText } from "./richText.tsx";

const HEADINGS = ["text-xl", "text-lg", "text-base", "text-sm", "text-sm", "text-sm"] as const;

const ALIGN: Record<Exclude<Align, null>, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

function Pieces({ space, c }: { space: SpaceOut; c: readonly Inline[] }) {
  return (
    <>
      {c.map((p, i) => {
        switch (p.t) {
          case "text":
            // biome-ignore lint/suspicious/noArrayIndexKey: pieces of one text, in order, never reordered.
            return <RichText key={i} space={space} text={p.v} inline={true} />;
          case "code":
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: pieces of one text, in order, never reordered.
              <code key={i} class="rounded bg-zinc-100 px-1 py-px font-mono text-[0.9em]">
                {p.v}
              </code>
            );
          case "link":
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: pieces of one text, in order, never reordered.
              <a key={i} href={p.href} target="_blank" rel="noopener noreferrer" class={LINK}>
                <Pieces space={space} c={p.c} />
              </a>
            );
          case "strong":
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: pieces of one text, in order, never reordered.
              <strong key={i} class="font-semibold">
                <Pieces space={space} c={p.c} />
              </strong>
            );
          case "em":
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: pieces of one text, in order, never reordered.
              <em key={i}>
                <Pieces space={space} c={p.c} />
              </em>
            );
          default:
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: pieces of one text, in order, never reordered.
              <del key={i}>
                <Pieces space={space} c={p.c} />
              </del>
            );
        }
      })}
    </>
  );
}

function BlockView({ space, b }: { space: SpaceOut; b: Block }) {
  switch (b.t) {
    case "h": {
      const Tag = `h${Math.min(6, b.level + 1)}` as "h2";
      return (
        <Tag class={`font-semibold text-zinc-900 ${HEADINGS[b.level - 1] ?? "text-sm"}`}>
          <Pieces space={space} c={b.c} />
        </Tag>
      );
    }
    case "p":
      return (
        <p class="break-words">
          <Pieces space={space} c={b.c} />
        </p>
      );
    case "code":
      return (
        <pre class="overflow-x-auto rounded-md bg-zinc-100 p-3 font-mono text-xs">
          <code>{b.v}</code>
        </pre>
      );
    case "quote":
      return (
        <blockquote class="flex flex-col gap-2 border-l-2 border-zinc-300 pl-3 text-zinc-600">
          <Blocks space={space} list={b.c} />
        </blockquote>
      );
    case "list": {
      const items = b.items.map((it, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: a list's items, in order, never reordered.
        <li key={i} class="pl-1">
          <Blocks space={space} list={it} tight={true} />
        </li>
      ));
      return b.ordered ? (
        <ol start={b.start} class="flex list-decimal flex-col gap-1 pl-5">
          {items}
        </ol>
      ) : (
        <ul class="flex list-disc flex-col gap-1 pl-5">{items}</ul>
      );
    }
    case "table":
      return (
        <div class="overflow-x-auto">
          <table class="min-w-full border-collapse text-left text-sm">
            <thead>
              <tr class="border-b border-zinc-300">
                {b.head.map((h, k) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: a table's columns, in order.
                  <th key={k} class={`px-2 py-1 font-semibold ${ALIGN[b.align[k] ?? "left"]}`}>
                    <Pieces space={space} c={h} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {b.rows.map((row, r) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: a table's rows, in order.
                <tr key={r} class="border-b border-zinc-200 last:border-0">
                  {row.map((cell, k) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: a table's columns, in order.
                    <td key={k} class={`px-2 py-1 align-top ${ALIGN[b.align[k] ?? "left"]}`}>
                      <Pieces space={space} c={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return <hr class="border-zinc-200" />;
  }
}

function Blocks({ space, list, tight = false }: { space: SpaceOut; list: readonly Block[]; tight?: boolean }) {
  return (
    <div class={`flex flex-col ${tight ? "gap-1" : "gap-3"}`}>
      {list.map((b, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: a text's blocks, in order, never reordered.
        <BlockView key={i} space={space} b={b} />
      ))}
    </div>
  );
}

/** `text` (Markdown) drawn. */
export function Markdown({ space, text, class: cls = "" }: { space: SpaceOut; text: string; class?: string }) {
  return (
    <div class={`text-sm text-zinc-800 ${cls}`} data-selectable="">
      <Blocks space={space} list={blocks(text)} />
    </div>
  );
}
